'use client';

import { fetcher } from '@insta-club/api';
import { useCustomForm } from '@insta-club/hooks';
import { Invoice } from '@insta-club/models';
import { t } from '@insta-club/translations';
import { InvoiceDiscountFormValues, InvoiceDiscountSchema } from '@insta-club/validator';
import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

import Accordion from 'src/libs/ui/accordion';

interface Props {
    invoiceId: number;
    onChange: (amount: number) => void;
    discountCode?: string | undefined;
}

const DiscountForm: React.FC<Props> = ({ invoiceId, discountCode, onChange }) => {
    const [appliedCode, setAppliedCode] = useState(discountCode);
    const [error, setError] = useState('');
    const { register, handleSubmit, setValue, formState } = useCustomForm(InvoiceDiscountSchema, {
        code: discountCode ?? ''
    });
    const { isSubmitting, isValid } = formState;

    const onSubmit: SubmitHandler<InvoiceDiscountFormValues> = async ({ code }) => {
        const path = appliedCode ? '/discounts/remove' : '/discounts/apply';
        try {
            setError('');
            const res: Invoice = await fetcher(path, 'dashboard', 'POST', {
                body: {
                    discount_code: code.trim(),
                    invoice_id: invoiceId
                }
            });
            if (appliedCode) {
                setValue('code', '');
            }
            setAppliedCode(res.discount?.code);
            onChange(res.discount_amount);
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError('خطای ناشناخته رخ داده است');
            } else {
                setError((err as { message: string })?.message || 'خطای سرور رخ داده است');
            }
        }
    };

    return (
        <Accordion
            className='border-none bg-gray-100'
            items={[
                {
                    open: !!appliedCode,
                    title: t('discount_code'),
                    chevronTitle: t('import'),
                    chevronClassName: 'text-primary-500',
                    extera: <p className='text-sm text-gray-500'>{t('discount_code_hint')}</p>,
                    children: (
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className='flex w-full items-center gap-2 rounded-xl bg-white px-6 py-2'>
                                {appliedCode ? (
                                    <span className='flex-1 text-gray-400'>{appliedCode}</span>
                                ) : (
                                    <input
                                        {...register('code', { required: true })}
                                        placeholder={t('enter_discount_code')}
                                        className='h-full min-w-0 flex-1 bg-transparent text-gray-900 outline-none'
                                        autoComplete='off'
                                    />
                                )}
                                <button
                                    type='submit'
                                    disabled={isSubmitting || !isValid}
                                    className='cursor-pointer p-1 text-base text-gray-900 disabled:cursor-not-allowed disabled:text-gray-300'
                                >
                                    {appliedCode ? t('delete') : t('register')}
                                </button>
                            </div>
                            {error ? <p className='mt-2 mr-1 text-xs text-red-500'>{error}</p> : null}
                        </form>
                    )
                }
            ]}
        />
    );
};

export default DiscountForm;

